import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { ClinicMapLocation } from '@chiroup/core/types/ClinicMapLocation.type';
import { ChartBar as ChartBarIcon, ChevronUp as ChevronUpIcon } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { getStateName, normalizeStateToAbbrev, StateInfo } from './constants';
import StatBar from './StatBar';

interface StatsPanelProps {
  locations: ClinicMapLocation[];
  availableStates: StateInfo[];
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

const StatsPanel = ({ locations, availableStates, isOpen, onClose, onToggle }: StatsPanelProps) => {
  const [selectedState, setSelectedState] = useState<string>('');
  const [statsStatusFilter, setStatsStatusFilter] = useState<'all' | 'active' | 'churned'>('all');
  const [statsStateSearch, setStatsStateSearch] = useState<string>('');
  const [isStateSelectorExpanded, setIsStateSelectorExpanded] = useState(true);

  // Filtered states for Stats dropdown search
  const filteredStatsStates = useMemo(() => {
    if (!statsStateSearch.trim()) return availableStates;
    const query = statsStateSearch.toLowerCase().trim();
    return availableStates.filter(
      state =>
        state.fullName.toLowerCase().includes(query) || state.abbrev.toLowerCase().includes(query)
    );
  }, [availableStates, statsStateSearch]);

  // Calculate stats for selected state
  const stateStats = useMemo(() => {
    if (!selectedState) return null;

    // Get all clinics in the state first (for status counts)
    const allStateClinics = locations.filter(
      loc => normalizeStateToAbbrev(loc.state) === selectedState
    );
    const totalInState = allStateClinics.length;
    if (totalInState === 0) return null;

    // Status breakdown (always show total counts regardless of filter)
    const activeCountTotal = allStateClinics.filter(loc => !loc.churned).length;
    const churnedCountTotal = allStateClinics.filter(loc => loc.churned).length;
    const churnRate =
      totalInState > 0 ? ((churnedCountTotal / totalInState) * 100).toFixed(1) : '0';

    // Filter clinics based on status filter
    const stateClinics =
      statsStatusFilter === 'all'
        ? allStateClinics
        : statsStatusFilter === 'active'
          ? allStateClinics.filter(loc => !loc.churned)
          : allStateClinics.filter(loc => loc.churned);

    const total = stateClinics.length;
    if (total === 0) {
      return {
        total: 0,
        totalInState,
        planCounts: { ehr: 0, premium: 0, plus: 0, basic: 0 },
        activeCount: activeCountTotal,
        churnedCount: churnedCountTotal,
        churnRate,
        scheduleA: 0,
        scheduleB: 0,
        noSchedule: 0,
        selfPost: 0,
        automated: 0,
        noPostType: 0,
        platformCounts: { Facebook: 0, Instagram: 0, Linkedin: 0 },
        topCities: [] as [string, number][],
      };
    }

    // Plan breakdown
    const planCounts: Record<string, number> = { ehr: 0, premium: 0, plus: 0, basic: 0 };
    stateClinics.forEach(loc => {
      const plan = loc.plan?.toLowerCase() || 'unknown';
      if (plan in planCounts) planCounts[plan]++;
    });

    // Posting schedule breakdown
    const scheduleA = stateClinics.filter(loc => loc.postingSchedule === 'A').length;
    const scheduleB = stateClinics.filter(loc => loc.postingSchedule === 'B').length;
    const noSchedule = stateClinics.filter(loc => !loc.postingSchedule).length;

    // Post type breakdown
    const selfPost = stateClinics.filter(loc => loc.postType === 'Self-post').length;
    const automated = stateClinics.filter(loc => loc.postType === 'Automated').length;
    const noPostType = stateClinics.filter(loc => !loc.postType).length;

    // Platform usage
    const platformCounts: Record<string, number> = { Facebook: 0, Instagram: 0, Linkedin: 0 };
    stateClinics.forEach(loc => {
      if (loc.postingPlatforms) {
        if (loc.postingPlatforms.includes('Facebook')) platformCounts.Facebook++;
        if (loc.postingPlatforms.includes('Instagram')) platformCounts.Instagram++;
        if (loc.postingPlatforms.includes('Linkedin')) platformCounts.Linkedin++;
      }
    });

    // Top cities
    const cityCounts: Record<string, number> = {};
    stateClinics.forEach(loc => {
      if (loc.city) {
        cityCounts[loc.city] = (cityCounts[loc.city] || 0) + 1;
      }
    });
    const topCities = Object.entries(cityCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5) as [string, number][];

    return {
      total,
      totalInState,
      planCounts,
      activeCount: activeCountTotal,
      churnedCount: churnedCountTotal,
      churnRate,
      scheduleA,
      scheduleB,
      noSchedule,
      selfPost,
      automated,
      noPostType,
      platformCounts,
      topCities,
    };
  }, [selectedState, locations, statsStatusFilter]);

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`inline-flex items-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-md hover:bg-gray-50 ${
          isOpen ? 'border-primary-500 text-primary-700' : 'border-gray-300 text-gray-700'
        }`}
      >
        <ChartBarIcon className="h-5 w-5" />
        Stats
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[999]" onClick={onClose} />
          <div className="absolute right-0 z-[1001] mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <span className="font-medium text-gray-900">State Statistics</span>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <Icon icon={Icons.close} className="h-5 w-5" />
              </button>
            </div>

            {/* State Selector - Collapsible */}
            <div className="border-b border-gray-100 px-4 py-3">
              <button
                onClick={() => setIsStateSelectorExpanded(!isStateSelectorExpanded)}
                className="flex w-full items-center justify-between"
              >
                <span className="text-sm font-medium text-gray-700">
                  {selectedState ? (
                    <span className="flex items-center gap-2">
                      <span className="text-gray-500">State:</span>
                      <span className="font-semibold text-primary-700">
                        {getStateName(selectedState)}
                      </span>
                      <span className="text-xs text-gray-400">({selectedState})</span>
                    </span>
                  ) : (
                    'Select State/Province'
                  )}
                </span>
                {isStateSelectorExpanded ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <Icon icon={Icons.chevronDown} className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {isStateSelectorExpanded && (
                <div className="mt-3">
                  <div className="relative mb-2">
                    <Icon
                      icon={Icons.search}
                      className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search states..."
                      value={statsStateSearch}
                      onChange={e => setStatsStateSearch(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-8 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    {statsStateSearch && (
                      <button
                        onClick={() => setStatsStateSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <Icon icon={Icons.close} className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200">
                    {filteredStatsStates.length === 0 ? (
                      <div className="px-3 py-2 text-center text-sm text-gray-500">
                        No states found
                      </div>
                    ) : (
                      filteredStatsStates.map(state => (
                        <button
                          key={state.abbrev}
                          onClick={() => {
                            setSelectedState(state.abbrev);
                            setStatsStateSearch('');
                            setIsStateSelectorExpanded(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                            selectedState === state.abbrev
                              ? 'bg-primary-50 text-primary-700'
                              : 'text-gray-700'
                          }`}
                        >
                          <span>
                            {state.fullName} <span className="text-gray-400">({state.abbrev})</span>
                          </span>
                          <span className="text-xs text-gray-400">{state.count}</span>
                        </button>
                      ))
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    {availableStates.length} states/provinces with clinics
                  </p>
                </div>
              )}
            </div>

            {/* Status Filter */}
            {selectedState && (
              <div className="border-b border-gray-100 px-4 py-3">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Filter by Status
                </label>
                <div className="flex rounded-lg border border-gray-300 bg-gray-50 p-0.5">
                  <button
                    onClick={() => setStatsStatusFilter('all')}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      statsStatusFilter === 'all'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setStatsStatusFilter('active')}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      statsStatusFilter === 'active'
                        ? 'bg-green-500 text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setStatsStatusFilter('churned')}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      statsStatusFilter === 'churned'
                        ? 'bg-red-500 text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Churned
                  </button>
                </div>
              </div>
            )}

            {/* Stats Content */}
            {stateStats ? (
              <div className="max-h-[50vh] overflow-y-auto p-4">
                {/* State Header */}
                <div className="mb-3 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 px-3 py-2">
                  <h3 className="text-base font-semibold text-primary-900">
                    {getStateName(selectedState)}
                  </h3>
                  <p className="text-xs text-primary-600">{selectedState}</p>
                </div>

                {/* Overview */}
                <div className="mb-4">
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">Overview</h4>
                  {statsStatusFilter !== 'all' && stateStats.totalInState && (
                    <p className="mb-2 text-xs text-gray-500">
                      Showing {stateStats.total} {statsStatusFilter} of {stateStats.totalInState}{' '}
                      total clinics
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-gray-50 p-2 text-center">
                      <div className="text-xl font-bold text-gray-900">{stateStats.total}</div>
                      <div className="text-xs text-gray-500">
                        {statsStatusFilter === 'all'
                          ? 'Total Clinics'
                          : statsStatusFilter === 'active'
                            ? 'Active Clinics'
                            : 'Churned Clinics'}
                      </div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-2 text-center">
                      <div
                        className={`text-xl font-bold ${
                          parseFloat(stateStats.churnRate) > 10 ? 'text-red-600' : 'text-green-600'
                        }`}
                      >
                        {stateStats.churnRate}%
                      </div>
                      <div className="text-xs text-gray-500">Churn Rate (Overall)</div>
                    </div>
                  </div>
                </div>

                {/* Status - Only show when filter is "all" */}
                {statsStatusFilter === 'all' && (
                  <div className="mb-4">
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">Status</h4>
                    <div className="space-y-1">
                      <StatBar
                        label="Active"
                        count={stateStats.activeCount}
                        total={stateStats.totalInState || stateStats.total}
                        color="#10B981"
                      />
                      <StatBar
                        label="Churned"
                        count={stateStats.churnedCount}
                        total={stateStats.totalInState || stateStats.total}
                        color="#EF4444"
                      />
                    </div>
                  </div>
                )}

                {/* Quick Status Summary when filtered */}
                {statsStatusFilter !== 'all' && stateStats.totalInState && (
                  <div className="mb-4 rounded-lg bg-gray-50 p-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">State totals:</span>
                      <div className="flex gap-3">
                        <span className="text-green-600">{stateStats.activeCount} active</span>
                        <span className="text-red-600">{stateStats.churnedCount} churned</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Plans */}
                <div className="mb-4">
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">Plan Breakdown</h4>
                  <div className="space-y-1">
                    <StatBar
                      label="EHR"
                      count={stateStats.planCounts.ehr}
                      total={stateStats.total}
                      color="#10B981"
                    />
                    <StatBar
                      label="Premium"
                      count={stateStats.planCounts.premium}
                      total={stateStats.total}
                      color="#8B5CF6"
                    />
                    <StatBar
                      label="Plus"
                      count={stateStats.planCounts.plus}
                      total={stateStats.total}
                      color="#3B82F6"
                    />
                    <StatBar
                      label="Basic"
                      count={stateStats.planCounts.basic}
                      total={stateStats.total}
                      color="#6B7280"
                    />
                  </div>
                </div>

                {/* Posting Schedule */}
                <div className="mb-4">
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">Posting Schedule</h4>
                  <div className="space-y-1">
                    <StatBar
                      label="Schedule A"
                      count={stateStats.scheduleA}
                      total={stateStats.total}
                      color="#F59E0B"
                    />
                    <StatBar
                      label="Schedule B"
                      count={stateStats.scheduleB}
                      total={stateStats.total}
                      color="#06B6D4"
                    />
                    <StatBar
                      label="Not Set"
                      count={stateStats.noSchedule}
                      total={stateStats.total}
                      color="#9CA3AF"
                    />
                  </div>
                </div>

                {/* Platforms */}
                <div className="mb-4">
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">Platform Usage</h4>
                  <div className="space-y-1">
                    <StatBar
                      label="Facebook"
                      count={stateStats.platformCounts.Facebook}
                      total={stateStats.total}
                      color="#1877F2"
                    />
                    <StatBar
                      label="Instagram"
                      count={stateStats.platformCounts.Instagram}
                      total={stateStats.total}
                      color="#E4405F"
                    />
                    <StatBar
                      label="LinkedIn"
                      count={stateStats.platformCounts.Linkedin}
                      total={stateStats.total}
                      color="#0A66C2"
                    />
                  </div>
                </div>

                {/* Top Cities */}
                {stateStats.topCities.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">Top Cities</h4>
                    <div className="space-y-1">
                      {stateStats.topCities.map(([city, count]) => (
                        <div key={city} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{city}</span>
                          <span className="text-gray-500">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-gray-500">
                Select a state to view statistics
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(StatsPanel);
